import { type PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { useQueryClient } from '@tanstack/react-query';
import { type Language, type MarketCountryCode } from '@pkgs/api';
import { Chatbot, ChatbotProvider } from '@ev-tech/search-modules-chatbot';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { SEARCH_API_CONFIG } from '@/consts/search-api';

type AppChatbotProviderProps = PropsWithChildren<{
  countryCode: MarketCountryCode;
  language: Language;
  enabled: boolean;
}>;

const { baseURL, ...apiConfig } = SEARCH_API_CONFIG;

export const AppChatbotProvider = ({ children, countryCode, language, enabled }: AppChatbotProviderProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.chatbot);
  const context = useMemo(() => ({ marketCountryCode: countryCode, language }), [countryCode, language]);

  return (
    <ChatbotProvider
      conversationKey={`search-preview:${countryCode}:${language}`}
      queryClient={queryClient}
      translationResolver={t}
      apiConfig={apiConfig}
      context={context}
    >
      {children}
      {enabled && <Chatbot />}
    </ChatbotProvider>
  );
};
